import { useEffect, useState } from 'react';
import { outboundAlertWebhook, outboundAlertWebhookSave, outboundAlertWebhookTest, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, btn, chip, pre, sx, useApi, useGuarded } from './kit';

// Restored from the Flutter console (integrations_view.dart:198-226, no design control):
// where every alert is sent outside Aithora, which severities go, and a test delivery.
// Aithora operators only; the server refuses everyone else and never returns the secret.

const SEVS = ['WARN', 'CRITICAL', 'KILL'] as const;
const INPUT = 'box-sizing:border-box;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;min-width:0;';

export function OutboundAlerts() {
  const { client } = useSession();
  const { wr } = useGuarded();
  const cfg = useApi((c, s) => outboundAlertWebhook(c, { signal: s }), []);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [sev, setSev] = useState<string[]>(['WARN', 'CRITICAL', 'KILL']);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  useEffect(() => {
    if (!cfg.data) return;
    setUrl(cfg.data.url ?? '');
    setSev(cfg.data.severities ?? ['WARN', 'CRITICAL', 'KILL']);
  }, [cfg.data]);
  if (cfg.error && cfg.error.kind === 'forbidden') return null;

  const run = async (fn: () => Promise<string>) => {
    setErr(null);
    setNote(null);
    try { setNote(await fn()); } catch (e) { setErr(e as ApiError); }
  };
  const save = () => run(async () => {
    await outboundAlertWebhookSave(client, { body: { url: url.trim(), severities: sev, ...(secret ? { secret } : {}) } });
    setSecret('');
    cfg.reload();
    return 'Saved.';
  });
  const test = () => run(async () => {
    const r = await outboundAlertWebhookTest(client, {});
    return r.delivered ? 'Test delivered.' : `Test sent, not delivered: ${r.error ?? 'no answer'}.`;
  });

  return (
    <div data-testid="settings-outbound-alerts" style={sx('min-width:0;padding:8px 0 10px;border-bottom:1px solid rgba(34,39,46,.07);margin-bottom:6px;')}>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:700;')}>{I('Alert webhook')}</span>
        <span style={sx(cfg.data?.configured ? chip('#12503C', 'rgba(47,138,111,.16)') : chip('#54606C', 'rgba(34,39,46,.08)'))}>{I(cfg.data?.configured ? 'SET' : 'NOT SET')}</span>
        <span style={sx('font-size:16px;color:#54606C;')}>{I('Every alert of the chosen severities is also sent here.')}</span>
      </div>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:8px;')}>
        <input aria-label="Alert webhook URL" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" style={sx(INPUT + 'flex:2 1 240px;')} />
        <input aria-label="Signing secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={cfg.data?.hasSecret ? 'Secret kept (type to replace)' : 'Signing secret (optional)'} style={sx(INPUT + 'flex:1 1 160px;')} />
        {SEVS.map((s) => {
          const on = sev.includes(s);
          return (
            <a key={s} href="#" role="checkbox" aria-checked={on} onClick={(e) => { pre(e); setSev(on ? sev.filter((x) => x !== s) : [...sev, s]); }} style={sx(on ? chip('#264C7E', 'rgba(47,92,151,.14)') : chip('#54606C', 'rgba(34,39,46,.06)'))}>
              {I(s)}
            </a>
          );
        })}
        <a href="#" {...wr(() => void save())} style={sx(btn('primary'))}>Save</a>
        <a href="#" {...wr(() => void test())} style={sx(btn(cfg.data?.configured ? 'soft' : 'off'))}>Send test</a>
      </div>
      {note && <div style={sx('font-size:16px;color:#2F5C97;margin-top:6px;')}>{I(note)}</div>}
      {(err || cfg.error) && <ErrLine error={err ?? cfg.error} style="margin:6px 0 0;" />}
    </div>
  );
}
